import { v4 as uuid } from 'uuid';
import { AppDispatch, AppState, store } from 'app/store';
import { SdkWrapperOptions } from '@pango-inc/keybox-sdk-wrapper/dist/constants/sdk-wrapper-init.constants';
import {
  selectAccessToken,
  selectRefreshToken,
} from 'services/session/sessionSelectors';
import * as lodash from 'lodash';

// Web Worker
import { WorkerChannel } from '@pango-inc/keybox-sdk-wrapper/dist/sdk-wrapper.main';
import VaultWorkerUrl from '@pango-inc/keybox-sdk-wrapper/dist/sdk-wrapper.worker-web?url';
import { ListenEvents } from '@pango-inc/keybox-sdk-wrapper/dist/types/communication/listen.types';
import {
  DocumentItem,
  DocumentWithFiles,
  FileItem,
  AccountItem,
} from '@pango-inc/vault.wasm/dist/vault/vault.types';
import { ENV, GENERAL } from 'utils/constants';
import { getSdkLogLevels } from './utils';
import { setAppInfo, setVaultAccounts } from './vaultSlice';

const broadcastChannelName = uuid();
const { LoggerConfig } = getSdkLogLevels();

type InitWorker = {
  userId: string;
  project: string;
};

// Worker Initialization
export function initWorker({ project, userId }: InitWorker) {
  if (typeof window === 'object' && window && window?.Worker) {
    const worker = new Worker(VaultWorkerUrl, {
      name: 'Vault Web Worker',
      type: 'module',
    });
    const PWMGR_ENV = ENV === 'prod' ? 'production' : 'staging';

    const initConfig: SdkWrapperOptions = {
      PROJECT_ID: project,
      VERSION: '1.0.0',
      PWMGR_ENV,
      BRAND: 'pango',
      DEVICE_TYPE: 'Web',
      LoggerConfig,
      AUTH_MODE: 'inside',
      BROADCAST_CHANNEL_NAME: broadcastChannelName,
      PANGO_AUTH_API_PASSWORD: '',
      PANGO_AUTH_API_USERNAME: '',
      USER_ID: userId,
      IS_TELEMETRY_ENABLED: true,
    };

    worker.postMessage({
      type: 'initMessage',
      payload: initConfig,
    });

    /* Terminate worker */
    return () => {
      worker.terminate();
    };
  }

  return () => {
    console.log('Sorry! No Web Worker support.');
  };
}

// New Worker Channel
const workerChannel = new WorkerChannel({
  instance: 'CONTENT',
  broadcastChannelName,
});

// Connect to Worker
workerChannel.connect();

/*
SDK Functions to Get Core information
*/
export function executeGetAppInfo() {
  return async () => {
    try {
      const appInfo = await workerChannel.sendRequest<'GetAppInfo'>({
        type: 'GetAppInfo',
        payload: {},
      });
      console.log(`App Info ${JSON.stringify(appInfo)}`);
      return appInfo.payload;
    } catch (error) {
      return false;
    }
  };
}

/*
Authentication Functions
*/

export function executeSetTokens() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const accessToken = selectAccessToken(getState());
      const refreshToken = selectRefreshToken(getState());
      if (accessToken && refreshToken) {
        const setTokensResult = await workerChannel.sendRequest<'SetTokens'>({
          type: 'SetTokens',
          payload: {
            accessToken,
            refreshToken,
          },
        });
        console.log(`set tokens ${JSON.stringify(setTokensResult)}`);
        return setTokensResult;
      }
      return Promise.reject(new Error(GENERAL.defaultErrorMsg));
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeUnlockVault(MasterPassword: string) {
  return async () => {
    try {
      const unlockResult = await workerChannel.sendRequest<'UnlockUser'>({
        type: 'UnlockUser',
        payload: {
          password: MasterPassword,
          savePassword: true,
        },
      });
      console.log(JSON.stringify(unlockResult));
      return unlockResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeLockVault() {
  return async () => {
    try {
      const lockResult = await workerChannel.sendRequest<'LockUser'>({
        type: 'LockUser',
        payload: {},
      });
      console.log(JSON.stringify(lockResult));
      return lockResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeExitVault() {
  return async () => {
    try {
      const lockResult = await workerChannel.sendRequest<'VaultExist'>({
        type: 'VaultExist',
        payload: {},
      });
      return lockResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
/* 
SDK Vault operations
*/
export function executeVaultExists() {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'VaultExist'>({
        type: 'VaultExist',
        payload: {},
      });
      return result.payload;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeCreateVault(
  masterPassword: string,
  recoveryKey: string,
) {
  return async () => {
    try {
      const createResult = await workerChannel.sendRequest<'CreateVault'>({
        type: 'CreateVault',
        payload: {
          masterPassword,
          recoveryKey,
        },
      });
      console.log(JSON.stringify(createResult));
      return createResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeUnlockUsingRecoveryKey(recoveryKey: string) {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'RecoveryUnlock'>({
        type: 'RecoveryUnlock',
        payload: {
          recoveryKey,
        },
      });
      console.log(JSON.stringify(result));
      return result.payload;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeChangeMasterPassword(
  masterPassword: string,
  recoveryKey: string,
) {
  return async () => {
    try {
      const result =
        await workerChannel.sendRequest<'ChangeCredentialsAndRecoveryKey'>({
          type: 'ChangeCredentialsAndRecoveryKey',
          payload: {
            masterPassword,
            recoveryKey,
          },
        });
      console.log(JSON.stringify(result));
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeEvaluatePasswordStrength(password: string) {
  return async () => {
    try {
      const result =
        await workerChannel.sendRequest<'EvaluatePasswordStrength'>({
          type: 'EvaluatePasswordStrength',
          payload: {
            password,
          },
        });
      console.log(JSON.stringify(result));
      return result.payload;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeGeneratePassword() {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'GeneratePassword'>({
        type: 'GeneratePassword',
        payload: {
          length: 12,
          /**
           * The minimal number of lowercase characters in the password or '-1' for none
           */
          lowercase: 2,
          /**
           * The minimal number of uppercase characters in the password or '-1' for none
           */
          uppercase: 2,
          /**
           * The minimal number of digits in the password or '-1' for none
           */
          digits: 2,
          /**
           * The minimal number of special symbols in the password or '-1' for none
           */
          symbols: 1,
          /**
           * Optional, overrides the default set of special symbols, if set ("!@#$%^&*", for example)
           */
          symbolSet: '!@#$%^&*',
        },
      });
      console.log(JSON.stringify(result));
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeGenerateRecoveryKey() {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'GenerateRecoveryKey'>({
        type: 'GenerateRecoveryKey',
        payload: {},
      });
      console.log(JSON.stringify(result));
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeGetUserProfile() {
  return async () => {
    try {
      const userProfile = await workerChannel.sendRequest<'GetUserProfile'>({
        type: 'GetUserProfile',
        payload: {},
      });
      console.log(JSON.stringify(userProfile));
      return userProfile;
    } catch (error) {
      return false;
    }
  };
}

/*
Password Manager Functions
*/

export function executeGetAccountItems() {
  return async () => {
    try {
      const accountItems = await workerChannel.sendRequest<'GetAccountItems'>({
        type: 'GetAccountItems',
        payload: {},
      });
      console.log(`Account from SDK ${JSON.stringify(accountItems)}`);
      return accountItems.payload;
    } catch (error) {
      return [];
    }
  };
}

export function executeSaveAccountItems(account: AccountItem[]) {
  return async () => {
    try {
      const saveResult = await workerChannel.sendRequest<'SaveAccountItems'>({
        type: 'SaveAccountItems',
        payload: account,
      });
      console.log(`executeSaveAccountItems ${JSON.stringify(saveResult)}`);
      return saveResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeDeleteAccountItem(id: string) {
  return async () => {
    try {
      const deleteResult =
        await workerChannel.sendRequest<'DeleteAccountItems'>({
          type: 'DeleteAccountItems',
          payload: [id],
        });
      console.log(JSON.stringify(deleteResult));
      return deleteResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeUpdateAccountItem(account: AccountItem) {
  return async () => {
    try {
      const updateResult =
        await workerChannel.sendRequest<'UpdateAccountItems'>({
          type: 'UpdateAccountItems',
          payload: [account],
        });
      console.log(JSON.stringify(updateResult));
      return updateResult.payload;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeSearchVaultAccounts(searchTerm: string) {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'SearchVaultAccounts'>({
        type: 'SearchVaultAccounts',
        payload: {
          titleSubstring: searchTerm,
          domain: searchTerm,
          username: searchTerm,
          plaintextPassword: searchTerm,
          maskedPassword: searchTerm,
          noteSubstring: searchTerm,
        },
      });
      console.log(JSON.stringify(result));
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeImportPasswordsFromFile(
  fileContentBase64: string,
  fileName: string,
) {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'ImportPasswordsFromFile'>(
        {
          type: 'ImportPasswordsFromFile',
          payload: {
            fileContentBase64,
            fileName,
          },
        },
      );
      console.log(JSON.stringify(result));
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeExportAccountsFromVault() {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'ExportAccountsFromVault'>(
        {
          type: 'ExportAccountsFromVault',
          payload: {},
        },
      );
      console.log(JSON.stringify(result));
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeRevealPassword(accountId: string) {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'RevealPassword'>({
        type: 'RevealPassword',
        payload: {
          accountId,
        },
      });
      console.log(JSON.stringify(result));
      return result.payload;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function executeGetCurrentAccountItemLimits() {
  return async () => {
    try {
      const result =
        await workerChannel.sendRequest<'GetCurrentAccountItemLimits'>({
          type: 'GetCurrentAccountItemLimits',
          payload: {},
        });
      console.log(JSON.stringify(result));
      return result.payload;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/* 
Digital Vault Functions
*/
export function executeDownloadAllFiles(documentId: string) {
  return async () => {
    try {
      const appInfo = await workerChannel.sendRequest<'DownloadAllFiles'>({
        type: 'DownloadAllFiles',
        payload: {
          documentId,
        },
      });
      console.log(JSON.stringify(appInfo));
      return appInfo.payload;
    } catch (error) {
      return false;
    }
  };
}

export function executeGetDocuments() {
  return async () => {
    try {
      const documents = await workerChannel.sendRequest<'GetDocuments'>({
        type: 'GetDocuments',
        payload: {},
      });
      console.log(JSON.stringify(documents));
      return documents.payload;
    } catch (error) {
      return false;
    }
  };
}

export function executeGetDocumentsByIds(ids: string[]) {
  return async () => {
    try {
      const documents = await workerChannel.sendRequest<'GetDocumentsByIds'>({
        type: 'GetDocumentsByIds',
        payload: ids,
      });
      console.log(JSON.stringify(documents));
      return documents;
    } catch (error) {
      return false;
    }
  };
}

export function executeAddDocuments(document: DocumentItem) {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'AddDocuments'>({
        type: 'AddDocuments',
        payload: [document],
      });
      console.log(JSON.stringify(result));
      return result.payload;
    } catch (error) {
      return false;
    }
  };
}

export function executeDeleteDocumentsById(id: string) {
  return async () => {
    try {
      const docs = await workerChannel.sendRequest<'DeleteDocumentsByIds'>({
        type: 'DeleteDocumentsByIds',
        payload: [id],
      });
      console.log(JSON.stringify(docs));
      return docs;
    } catch (error) {
      return false;
    }
  };
}

export function executeUpdateDocuments(document: DocumentWithFiles) {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'UpdateDocuments'>({
        type: 'UpdateDocuments',
        payload: [document],
      });
      console.log(JSON.stringify(result));
      return result.payload;
    } catch (error) {
      return false;
    }
  };
}

export function executeGetAccountItemLimits() {
  return async () => {
    try {
      const result =
        await workerChannel.sendRequest<'GetCurrentAccountItemLimits'>({
          type: 'GetCurrentAccountItemLimits',
          payload: {},
        });
      console.log(JSON.stringify(result));
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// File Operations
export function executeUploadFiles(
  documentId: string,
  files: Array<[FileItem]>,
) {
  return async () => {
    try {
      const docs = await workerChannel.sendRequest<'UploadFiles'>({
        type: 'UploadFiles',
        payload: {
          documentId,
          files,
        },
      });
      console.log(JSON.stringify(docs));
      return docs;
    } catch (error) {
      return false;
    }
  };
}

export function executeDeleteFiles(id: string) {
  return async () => {
    try {
      const docs = await workerChannel.sendRequest<'DeleteFiles'>({
        type: 'DeleteFiles',
        payload: [id],
      });
      console.log(JSON.stringify(docs));
      return docs.payload;
    } catch (error) {
      return false;
    }
  };
}

export function executeGetFileInfos(documentId: string) {
  return async () => {
    try {
      const docs = await workerChannel.sendRequest<'GetFileInfos'>({
        type: 'GetFileInfos',
        payload: {
          documentId,
        },
      });
      console.log(JSON.stringify(docs));
      return docs;
    } catch (error) {
      return false;
    }
  };
}

export function executeDownloadFiles(id: string) {
  return async () => {
    try {
      const result = await workerChannel.sendRequest<'DownloadFiles'>({
        type: 'DownloadFiles',
        payload: [id],
      });
      console.log(JSON.stringify(result));
      return result.payload;
    } catch (error) {
      return false;
    }
  };
}

export function executeDownloadPreviews(ids: string[]) {
  return async () => {
    try {
      const docs = await workerChannel.sendRequest<'DownloadPreviews'>({
        type: 'DownloadPreviews',
        payload: ids,
      });
      console.log(JSON.stringify(docs));
      return docs;
    } catch (error) {
      return false;
    }
  };
}

/* SDK Listener Functions */
workerChannel.listenMessage((message: ListenEvents) => {
  console.log(`Message from SDK ${JSON.stringify(message)}`);
  // ChangeAppInfo
  if (message.type === 'ChangeAppInfo') {
    const appInfo = message.payload;
    (async (dispatch: AppDispatch) => {
      dispatch(setAppInfo(appInfo));
    })(store.dispatch);
  }
  // TODO: enable this once PubNub is ready
  // VaultAccountsChanged
  if (message.type === 'VaultAccountsChanged') {
    const accounts = message.payload;
    if (lodash.isArray(accounts)) {
      (async (dispatch: AppDispatch) => {
        dispatch(setVaultAccounts(accounts));
      })(store.dispatch);
    }
  }
  // UpdateAccounts
  if (message.type === 'UpdateAccounts') {
    const accounts = message.payload;
    if (lodash.isArray(accounts)) {
      (async (dispatch: AppDispatch) => {
        dispatch(setVaultAccounts(accounts));
      })(store.dispatch);
    }
  }
  // UserUnauthorized
  if (message.type === 'UserUnauthorized') {
    (async (dispatch: AppDispatch) => {
      dispatch(setVaultAccounts([]));
    })(store.dispatch);
  }
});
